/**
 * Column visibility for the two Ads Manager grids that have no column
 * catalogue — Search Terms and Negatives (ТЗ «перевод в Ads Manager» §7.1).
 *
 * Same storage as the Keywords columns (`oneads.prefs` via readPref/writePref),
 * different shape: Keywords stores the VISIBLE list, here we store the HIDDEN
 * set. That is what makes a key the user never saw default to visible — a saved
 * setting written before the column existed simply does not mention it, and
 * "not mentioned" reads as "shown" (§7.1: missing key → column on).
 */

import { useState } from "react";
import { readPref, writePref } from "@/lib/useLocalStorage";

export type FixedGrid = "search_terms" | "negative_keywords";

/** The one toggleable column today. The name is the header everywhere. */
export const TRANSLATION_COLUMN = "translation_en";
export const TRANSLATION_LABEL = "Translation";

export function hiddenColumnsKey(grid: FixedGrid): string {
  return `oneads.ads_manager.hidden_columns.v1.${grid}`;
}

export function loadHiddenColumns(grid: FixedGrid): string[] {
  const raw = readPref<unknown>(hiddenColumnsKey(grid), []);
  return Array.isArray(raw) ? raw.filter((k): k is string => typeof k === "string") : [];
}

export function isColumnVisible(hidden: readonly string[], key: string): boolean {
  return !hidden.includes(key);
}

/** New hidden set after switching `key` on or off; persisted. */
export function setColumnVisible(
  grid: FixedGrid,
  hidden: readonly string[],
  key: string,
  visible: boolean,
): string[] {
  const next = visible
    ? hidden.filter((k) => k !== key)
    : Array.from(new Set([...hidden, key]));
  writePref(hiddenColumnsKey(grid), next);
  return next;
}

/** Hidden set + toggle for one grid, read once per mount. */
export function useGridColumns(grid: FixedGrid): {
  visible: (key: string) => boolean;
  toggle: (key: string) => void;
} {
  const [hidden, setHidden] = useState<string[]>(() => loadHiddenColumns(grid));
  return {
    visible: (key) => isColumnVisible(hidden, key),
    toggle: (key) =>
      setHidden((h) => setColumnVisible(grid, h, key, !isColumnVisible(h, key))),
  };
}
